import React, { useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Tooltip,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Skeleton,
} from "@mui/material";
import { CustomTextBox } from "components/CustomComponents";
import { useForm, Controller } from "react-hook-form";
import { customAPI } from "services/utils/CustomAPI";
import {
  PrimaryButton,
  SecondaryButton,
} from "components/CustomComponents/DesignSystemButtons";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestDetailHeader } from "components/DesignSystem/RequestDetailHeader";
import {
  themes,
  infoStyle,
  toAlignEnd,
  childBoxStyle,
} from "services/constants";
import { designColors } from "services/constants/designTokens";
import { useSelector } from "react-redux";
import { showToast } from "services/utils/Status";
import { atleastOneCharacterWithSpace } from "services/constants/patterns";
import { minimum3Characters } from "services/constants/ErrorMessages";
import { CustomAutoComplete } from "components/CustomComponents";
import { CustomTimePicker } from "components/CustomComponents/CustomTimePicker";
import { CustomizedDatePicker } from "components/CustomComponents/CustomizedDatePicker";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useFetch } from "services/hooks/useFetch";
import { tooltipStyleProps } from "services/constants/Styles";
import { formatBreakDuration } from "./attendance/attendanceHelpers";

dayjs.extend(customParseFormat);

export const CreateWorkAdjustment = ({
  open = true,
  onClose,
  reCallApi,
  defaultType,
  // "break" renders the dedicated Manual Break / Cancel Break request modal;
  // "general" keeps the full Work Adjustment form (Permission / On Duty / Comp Off).
  mode = "general",
}) => {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const isBreakMode = mode === "break";
  const {
    setValue,
    handleSubmit,
    register,
    watch,
    control,
    reset,
    clearErrors,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      user_name: "",
      attendance_type: "",
      break_category: "OT Break",
      button_loader: false,
      start_date: null,
      startTime: null,
      endTime: null,
      break_to_cancel: [],
      reason_for_work_adjustment: "",
    },
  });
  const { domain, userDetails } = useSelector((state) => state.tokenReducer);
  const { result: initialBreakData } = useFetch(
    `${domain}team_zone/approve/work_adjustment/user-break-context/?user_id=${userDetails?.id}&company_id=${requestCompanyId}`,
  );
  const attendanceDate = watch("attandance_date");
  const normalizedAttendanceDate = dayjs.isDayjs(attendanceDate)
    ? attendanceDate
    : dayjs(
        String(attendanceDate || ""),
        ["DD/MM/YYYY", "YYYY-MM-DD", "MM/DD/YYYY"],
        true,
      );
  const isValidAttendanceDate =
    normalizedAttendanceDate.isValid() &&
    normalizedAttendanceDate.year() >= 1900 &&
    normalizedAttendanceDate.year() <= 9999;
  const shouldFetchBreakData =
    isValidAttendanceDate && watch("attendance_type") !== "Cancel Break";
  const { result: breakData, setLoad: reloadBreakData } = useFetch(
    shouldFetchBreakData
      ? `${domain}team_zone/approve/work_adjustment/user-break-context/?user_id=${userDetails?.id}&date=${dayjs(
          normalizedAttendanceDate,
        ).format("YYYY-MM-DD")}&company_id=${requestCompanyId}`
      : null,
  );
  const shouldFetchCancelBreakOptions =
    isValidAttendanceDate && watch("attendance_type") === "Cancel Break";
  const {
    result: cancelBreakApiData,
    setLoad: reloadCancelBreakOptions,
    loading: cancelBreakLoading,
  } =
    useFetch(
      shouldFetchCancelBreakOptions
        ? `${domain}my_zone/apply/work_adjustment/cancel-break-options/?user_id=${userDetails?.id}&date=${dayjs(
            normalizedAttendanceDate,
          ).format("YYYY-MM-DD")}&company_id=${requestCompanyId}`
        : null,
    );
  const breakCategory = watch("break_category");
  // Break requests are cancel-only: "Cancel Break" is the fixed default.
  const effectiveDefaultType = isBreakMode ? "Cancel Break" : defaultType;
  const selectedAttendanceType =
    watch("attendance_type") || effectiveDefaultType;
  const isCancelBreakLoading =
    shouldFetchCancelBreakOptions && Boolean(cancelBreakLoading);
  const requestTypeLabel = {
    compoff: "Comp Off",
    "Comp off": "Comp Off",
    "Comp Off": "Comp Off",
    permission: "Permission",
    Permission: "Permission",
    onduty: "On Duty",
    "On Duty": "On Duty",
    Break: "Break",
    "Cancel Break": "Break",
  }[selectedAttendanceType || ""] || (isBreakMode ? "Break" : "Work Adjustment");
  const breakStartTime = watch("startTime");
  const breakEndTime = watch("endTime");
  const shiftDetails = breakData?.date_stats?.shift_details;
  const shiftStartTime = breakData?.date_stats?.shift_details?.shift_start_time;
  const shiftEndTime = breakData?.date_stats?.shift_details?.shift_end_time;
  const isOvernightShift = Boolean(shiftDetails?.is_overnight_shift);
  const getTimeInMinutes = (timeValue) =>
    dayjs(timeValue).hour() * 60 + dayjs(timeValue).minute();
  const shiftStartMinutes = shiftStartTime
    ? getTimeInMinutes(dayjs(shiftStartTime, "HH:mm"))
    : null;
  const shiftEndMinutes = shiftEndTime
    ? getTimeInMinutes(dayjs(shiftEndTime, "HH:mm"))
    : null;
  const isTimeWithinShiftWindow = (timeValue, view = "minutes") => {
    if (
      shiftStartMinutes === null ||
      shiftEndMinutes === null ||
      !timeValue ||
      !dayjs(timeValue).isValid()
    ) {
      return false;
    }

    const candidateMinutes = getTimeInMinutes(dayjs(timeValue));

    if (view === "hours") {
      const hourStartMinutes = dayjs(timeValue).hour() * 60;
      const hourEndMinutes = hourStartMinutes + 59;

      return isOvernightShift
        ? hourEndMinutes >= shiftStartMinutes ||
            hourStartMinutes <= shiftEndMinutes
        : hourEndMinutes >= shiftStartMinutes &&
            hourStartMinutes <= shiftEndMinutes;
    }

    return isOvernightShift
      ? candidateMinutes >= shiftStartMinutes ||
          candidateMinutes <= shiftEndMinutes
      : candidateMinutes >= shiftStartMinutes &&
          candidateMinutes <= shiftEndMinutes;
  };
  const shiftMinTime =
    watch("attendance_type") === "Break" &&
    breakCategory === "Shift Break" &&
    shiftStartTime &&
    !isOvernightShift
      ? dayjs(shiftStartTime, "HH:mm")
      : null;
  const shiftMaxTime =
    watch("attendance_type") === "Break" &&
    breakCategory === "Shift Break" &&
    shiftEndTime &&
    !isOvernightShift
      ? dayjs(shiftEndTime, "HH:mm")
      : null;
  const shiftDurationInMinutes =
    shiftStartTime && shiftEndTime
      ? isOvernightShift
        ? 24 * 60 - shiftStartMinutes + shiftEndMinutes
        : shiftEndMinutes - shiftStartMinutes
      : null;
  const otRecords = breakData?.date_stats?.ot_records || [];
  const sortOtRecordsByStartTime = (items = []) =>
    [...items].sort((a, b) => {
      const first = dayjs(a?.ot_start_datetime);
      const second = dayjs(b?.ot_start_datetime);

      if (!first.isValid() || !second.isValid()) {
        return 0;
      }

      return first.valueOf() - second.valueOf();
    });
  const sortedOtRecords = sortOtRecordsByStartTime(otRecords);
  const disableBreakTime = (value, view) => {
    if (
      watch("attendance_type") !== "Break" ||
      !shiftStartTime ||
      !shiftEndTime
    ) {
      return false;
    }

    const isWithinShiftWindow = isTimeWithinShiftWindow(value, view);

    if (breakCategory === "Shift Break") {
      return !isWithinShiftWindow;
    }

    if (breakCategory === "OT Break") {
      if (view === "hours") {
        const hourStartMinutes = dayjs(value).hour() * 60;
        const hourEndMinutes = hourStartMinutes + 59;
        const hasTimeOutsideShift = isOvernightShift
          ? hourStartMinutes < shiftStartMinutes &&
            hourEndMinutes > shiftEndMinutes
          : hourStartMinutes < shiftStartMinutes ||
            hourEndMinutes > shiftEndMinutes;

        return !hasTimeOutsideShift;
      }

      return isWithinShiftWindow;
    }

    return false;
  };
  const getBreakDateTime = (timeValue) => {
    if (!isValidAttendanceDate || !timeValue || !dayjs(timeValue).isValid()) {
      return null;
    }

    const breakDateTime = dayjs(normalizedAttendanceDate)
      .hour(dayjs(timeValue).hour())
      .minute(dayjs(timeValue).minute())
      .second(0);
    const nextDayBreakDateTime = breakDateTime.add(1, "day");

    if (breakCategory === "OT Break" && otRecords.length) {
      const matchingOtRecord = sortedOtRecords.find((record) => {
        const otStart = dayjs(record?.ot_start_datetime);
        const otEnd = dayjs(record?.ot_end_datetime);

        if (!otStart.isValid() || !otEnd.isValid()) {
          return false;
        }

        return (
          ((breakDateTime.isSame(otStart) || breakDateTime.isAfter(otStart)) &&
            (breakDateTime.isSame(otEnd) || breakDateTime.isBefore(otEnd))) ||
          ((nextDayBreakDateTime.isSame(otStart) ||
            nextDayBreakDateTime.isAfter(otStart)) &&
            (nextDayBreakDateTime.isSame(otEnd) ||
              nextDayBreakDateTime.isBefore(otEnd)))
        );
      });

      if (matchingOtRecord) {
        const otStart = dayjs(matchingOtRecord?.ot_start_datetime);
        const otEnd = dayjs(matchingOtRecord?.ot_end_datetime);

        if (
          (nextDayBreakDateTime.isSame(otStart) ||
            nextDayBreakDateTime.isAfter(otStart)) &&
          (nextDayBreakDateTime.isSame(otEnd) ||
            nextDayBreakDateTime.isBefore(otEnd))
        ) {
          return nextDayBreakDateTime;
        }

        return breakDateTime;
      }
    }

    if (
      isOvernightShift &&
      shiftEndMinutes !== null &&
      getTimeInMinutes(dayjs(timeValue)) <= shiftEndMinutes
    ) {
      return breakDateTime.add(1, "day");
    }

    return breakDateTime;
  };
  const selectedBreakStartDateTime = getBreakDateTime(breakStartTime);
  const selectedBreakEndDateTime = getBreakDateTime(breakEndTime);
  const selectedBreakDuration =
    selectedBreakStartDateTime &&
    selectedBreakEndDateTime &&
    selectedBreakEndDateTime.isAfter(selectedBreakStartDateTime)
      ? `${selectedBreakEndDateTime.diff(
          selectedBreakStartDateTime,
          "minute",
        )} minutes`
      : initialBreakData?.manual_break_duration
        ? `${initialBreakData.manual_break_duration} minutes`
        : "-";
  const selectedBreakDurationMinutes =
    selectedBreakStartDateTime &&
    selectedBreakEndDateTime &&
    selectedBreakEndDateTime.isAfter(selectedBreakStartDateTime)
      ? selectedBreakEndDateTime.diff(selectedBreakStartDateTime, "minute")
      : initialBreakData?.manual_break_duration || 0;
  const configuredBreakDurationMinutes =
    breakData?.manual_break_duration ??
    initialBreakData?.manual_break_duration ??
    0;
  const manualShiftBreaks = [
    ...(breakData?.date_stats?.manual_breaks_pending || []),
    ...(breakData?.date_stats?.manual_breaks_approved || []),
  ].filter(
    (item) => String(item?.break_type || "").toLowerCase() === "shift_break",
  );
  const existingShiftBreaks = [
    ...(breakData?.date_stats?.real_punch_breaks || []),
    ...manualShiftBreaks,
  ];
  const isAttendanceValid = breakData?.date_stats?.is_attendance_valid;
  const isOtAllowed =
    breakData?.date_stats?.is_ot_allowed ?? breakData?.is_ot_allowed;
  const applyDisabledReason =
    watch("attendance_type") === "Break" && isValidAttendanceDate
      ? isAttendanceValid === false
        ? "No attendance record found on the selected date"
        : breakCategory === "OT Break" && isOtAllowed === false
          ? "Not eligible to apply OT break on the selected date"
          : ""
      : "";
  const manualOtBreaks = [
    ...(breakData?.date_stats?.manual_breaks_pending || []),
    ...(breakData?.date_stats?.manual_breaks_approved || []),
  ].filter(
    (item) => String(item?.break_type || "").toLowerCase() === "ot_break",
  );
  const existingOtBreaks = [
    ...otRecords.flatMap((record) => record?.ot_breaks || []),
    ...manualOtBreaks,
  ].filter(
    (item, index, arr) =>
      arr.findIndex(
        (other) =>
          (other?.start_time || other?.break_start_time) ===
            (item?.start_time || item?.break_start_time) &&
          (other?.end_time || other?.break_end_time) ===
            (item?.end_time || item?.break_end_time),
      ) === index,
  );
  const sortBreakItemsByStartTime = (items = []) =>
    [...items].sort((a, b) => {
      const first = dayjs(a?.start_time || a?.break_start_time);
      const second = dayjs(b?.start_time || b?.break_start_time);

      if (!first.isValid() || !second.isValid()) {
        return 0;
      }

      return first.valueOf() - second.valueOf();
    });
  const sortedShiftBreaks = sortBreakItemsByStartTime(existingShiftBreaks);
  const sortedOtBreaks = sortBreakItemsByStartTime(existingOtBreaks);
  const cancelBreakApiList = Array.isArray(cancelBreakApiData)
    ? cancelBreakApiData
    : Array.isArray(cancelBreakApiData?.break_options)
      ? cancelBreakApiData.break_options
      : [];
  const resolveCancelBreakCategory = (item) => {
    const label = item?.category_label || item?.type_label;
    if (label === "Shift Break" || label === "OT Break") {
      return {
        code: label === "Shift Break" ? "Shift_break" : "Ot_break",
        label,
      };
    }

    const code = String(
      item?.category_code || item?.break_type || "",
    ).toLowerCase();
    const isOt = ["ot_break", "ot break"].includes(code) || item?.over_time_id;
    const isShift = !isOt;
    return {
      code: isShift ? "Shift_break" : "Ot_break",
      label: isShift ? "Shift Break" : "OT Break",
    };
  };
  const cancelBreakOptions = cancelBreakApiList.map((item, index) => {
    const category = resolveCancelBreakCategory(item);
    return {
      ...item,
      cancel_row_id: item?.break_id ?? item?.id ?? index,
      category_code: category.code,
      category_label: category.label,
    };
  });
  const previousBreakCategoryRef = useRef();
  const previousAttendanceTypeRef = useRef();
  const renderBreakEntries = (breaks, emptyMessage) => {
    if (!breaks?.length) {
      return (
        <Typography sx={{ fontSize: "0.9rem", color: "text.secondary" }}>
          {emptyMessage}
        </Typography>
      );
    }

    return (
      <Box display="flex" flexDirection="column" gap={0.75}>
        <Grid container spacing={0.75} sx={{ px: 0.75 }}>
          <Grid item xs={4}>
            <Typography variant="caption" color="text.secondary">
              Start
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="caption" color="text.secondary">
              End
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="caption" color="text.secondary">
              Duration
            </Typography>
          </Grid>
        </Grid>
        {breaks.map((item, index) => (
          <Box
            key={
              item?.id ||
              `${item?.start_time || item?.break_start_time || "break"}_${index}`
            }
            sx={{
              p: 0.75,
              borderRadius: 1,
              backgroundColor: "#ffffff",
              border: `1px solid ${themes?.miliedLightGray}`,
            }}
          >
            <Grid container spacing={0.75}>
              <Grid item xs={4}>
                <Typography sx={{ lineHeight: 1.3 }}>
                  {item?.start_time || item?.break_start_time
                    ? dayjs(item?.start_time || item?.break_start_time).format(
                        "HH:mm",
                      )
                    : "-"}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography sx={{ lineHeight: 1.3 }}>
                  {item?.end_time || item?.break_end_time
                    ? dayjs(item?.end_time || item?.break_end_time).format(
                        "HH:mm",
                      )
                    : "-"}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography sx={{ lineHeight: 1.3 }}>
                  {formatBreakDuration(item?.duration)}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        ))}
      </Box>
    );
  };
  const validateOtBreakRange = (currentStart, currentEnd) => {
    if (!currentStart || !currentEnd) {
      return null;
    }

    const hasOtBreakOverlap = existingOtBreaks.some((item) => {
      const existingStart = dayjs(item?.start_time);
      const existingEnd = dayjs(item?.end_time);

      if (!existingStart.isValid() || !existingEnd.isValid()) {
        return false;
      }

      return (
        currentStart.isBefore(existingEnd) && currentEnd.isAfter(existingStart)
      );
    });

    if (hasOtBreakOverlap) {
      return "Selected OT break overlaps with an existing OT break";
    }

    return null;
  };
  const BreakContextInfo = () => {
    if (watch("attendance_type") !== "Break") {
      return null;
    }

    if (breakCategory === "Shift Break") {
      return (
        <Paper
          elevation={0}
          sx={{
            mt: 1,
            p: 1.5,
            border: `1px solid ${themes?.miliedLightGray}`,
            backgroundColor: "#f8fafc",
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: "1rem", mb: 1.25 }}>
            Shift Details
          </Typography>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={4}>
              <Typography
                sx={{ fontSize: "0.9rem", color: "text.secondary", mb: 0.5 }}
              >
                Shift Name
              </Typography>
              <Typography sx={{ fontSize: "1rem", fontWeight: 400 }}>
                {shiftDetails?.shift_name || "-"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography
                sx={{ fontSize: "0.9rem", color: "text.secondary", mb: 0.5 }}
              >
                Shift Start
              </Typography>
              <Typography sx={{ fontSize: "1rem", fontWeight: 400 }}>
                {shiftDetails?.shift_start_time || "-"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography
                sx={{ fontSize: "0.9rem", color: "text.secondary", mb: 0.5 }}
              >
                Shift End
              </Typography>
              <Typography sx={{ fontSize: "1rem", fontWeight: 400 }}>
                {shiftDetails?.shift_end_time || "-"}
              </Typography>
            </Grid>
          </Grid>
          <Box sx={{ mt: 1.5 }}>
            <Typography sx={{ fontWeight: 600, mb: 0.75 }}>
              Existing Shift Breaks
            </Typography>
            {renderBreakEntries(sortedShiftBreaks, "No shift breaks found")}
          </Box>
        </Paper>
      );
    }

    if (breakCategory === "OT Break") {
      if (!otRecords?.length) {
        return null;
      }

      return (
        <Paper
          elevation={0}
          sx={{
            mt: 1,
            p: 1.5,
            border: `1px solid ${themes?.miliedLightGray}`,
            backgroundColor: "#fffaf0",
          }}
        >
          <Typography sx={{ fontWeight: 700, mb: 1 }}>OT Details</Typography>
          <Box display="flex" flexDirection="column" gap={1}>
            <Grid container spacing={0.75} sx={{ px: 0.75 }}>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">
                  OT Start
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">
                  OT End
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">
                  Break Taken
                </Typography>
              </Grid>
            </Grid>
            {sortedOtRecords.map((record, index) => (
              <Box
                key={record?.ot_id || index}
                sx={{
                  p: 0.75,
                  borderRadius: 1,
                  backgroundColor: "#ffffff",
                  border: `1px solid ${themes?.miliedLightGray}`,
                }}
              >
                <Grid container spacing={0.75}>
                  <Grid item xs={4}>
                    <Typography sx={{ lineHeight: 1.3 }}>
                      {record?.ot_start_datetime
                        ? dayjs(record.ot_start_datetime).format("HH:mm")
                        : "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography sx={{ lineHeight: 1.3 }}>
                      {record?.ot_end_datetime
                        ? dayjs(record.ot_end_datetime).format("HH:mm")
                        : "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography sx={{ lineHeight: 1.3 }}>
                      {record?.total_ot_break_taken || "00:00"}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            ))}
          </Box>
          <Box sx={{ mt: 1.5 }}>
            <Typography sx={{ fontWeight: 600, mb: 0.75 }}>
              Existing OT Breaks
            </Typography>
            {renderBreakEntries(sortedOtBreaks, "No OT breaks found")}
          </Box>
        </Paper>
      );
    }

    return null;
  };

  // When opened from a type-specific tab's "New" button, preselect that
  // attendance type so the relevant fields render immediately.
  const didPresetTypeRef = useRef(false);
  useEffect(() => {
    if (effectiveDefaultType && !didPresetTypeRef.current) {
      didPresetTypeRef.current = true;
      setValue("attendance_type", effectiveDefaultType);
    }
  }, [effectiveDefaultType, setValue]);

  useEffect(() => {
    if (isValidAttendanceDate && selectedAttendanceType === "Cancel Break") {
      reloadBreakData(true);
      reloadCancelBreakOptions(true);
    }
  }, [
    attendanceDate,
    isValidAttendanceDate,
    selectedAttendanceType,
    reloadBreakData,
    reloadCancelBreakOptions,
  ]);

  useEffect(() => {
    const previousBreakCategory = previousBreakCategoryRef.current;
    const previousAttendanceType = previousAttendanceTypeRef.current;
    const hasSwitchedToOtBreak =
      previousBreakCategory !== undefined &&
      previousBreakCategory !== "OT Break" &&
      breakCategory === "OT Break";
    const hasSwitchedToShiftBreak =
      previousBreakCategory !== undefined &&
      previousBreakCategory !== "Shift Break" &&
      breakCategory === "Shift Break";
    const hasEnteredBreakWithOtCategory =
      previousAttendanceType !== undefined &&
      previousAttendanceType !== "Break" &&
      selectedAttendanceType === "Break" &&
      breakCategory === "OT Break";

    previousBreakCategoryRef.current = breakCategory;
    previousAttendanceTypeRef.current = selectedAttendanceType;

    if (hasSwitchedToOtBreak || hasSwitchedToShiftBreak) {
      setValue("startTime", null);
      setValue("endTime", null);
      clearErrors(["startTime", "endTime"]);
    }

    if (
      selectedAttendanceType !== "Break" ||
      breakCategory !== "OT Break" ||
      !sortedOtRecords.length
    ) {
      return;
    }

    if (
      !hasSwitchedToOtBreak &&
      !hasEnteredBreakWithOtCategory &&
      breakStartTime &&
      breakEndTime
    ) {
      return;
    }

    const defaultOtRecord = sortedOtRecords[sortedOtRecords.length - 1];
    const defaultStartTime = dayjs(defaultOtRecord?.ot_start_datetime);

    if (!defaultStartTime.isValid()) {
      return;
    }

    setValue("startTime", defaultStartTime);
    setValue(
      "endTime",
      defaultStartTime.add(configuredBreakDurationMinutes, "minute"),
    );
  }, [
    selectedAttendanceType,
    breakCategory,
    breakStartTime,
    breakEndTime,
    configuredBreakDurationMinutes,
    clearErrors,
    setValue,
    sortedOtRecords,
  ]);

  useEffect(() => {
    if (selectedAttendanceType !== "Cancel Break") {
      setValue("break_to_cancel", []);
    }
  }, [selectedAttendanceType, setValue]);

  const onSubmit = async (data) => {
    setValue("button_loader", true);
    let payload = {
      user_id: userDetails?.id,
      attendance_type:
        data?.attendance_type === "Break"
          ? "break"
          : data?.attendance_type === "Cancel Break"
            ? "cancel_break"
            : data?.attendance_type,
      reason: data?.reason_for_work_adjustment,
    };
    if (data?.attendance_type === "Cancel Break") {
      payload = {
        attendance_type: "Cancel Break",
        user_id: userDetails?.id,
        break_ids: (data?.break_to_cancel || []).map((entry) => entry?.id),
        reason: data?.reason_for_work_adjustment,
      };
    }
    if (data?.attendance_type === "Break") {
      payload.break_type =
        data?.break_category === "OT Break" ? "Ot_break" : "Shift_break";
      const calculatedBreakStartDateTime = getBreakDateTime(watch("startTime"));
      const calculatedBreakEndDateTime = getBreakDateTime(watch("endTime"));
      payload.break_start_time = calculatedBreakStartDateTime?.format(
        "YYYY-MM-DDTHH:mm:ssZ",
      );
      payload.break_end_time = calculatedBreakEndDateTime?.format(
        "YYYY-MM-DDTHH:mm:ssZ",
      );
      payload.duration = selectedBreakDurationMinutes;
      payload.date = normalizedAttendanceDate.format("YYYY-MM-DD");
    }
    if (
      data?.attendance_type === "On Duty" ||
      data?.attendance_type === "Permission"
    ) {
      payload.date = normalizedAttendanceDate.format("YYYY-MM-DD");
      payload.start_time = dayjs(watch("startTime")).format("HH:mm");
      payload.end_time = dayjs(watch("endTime")).format("HH:mm");
    }
    if (data?.attendance_type === "Comp off") {
      payload.earned_date = data?.earned_date?.format("YYYY-MM-DD");
      payload.requested_date =
        data?.comp_off_request_date?.format("YYYY-MM-DD");
    }
    const response = await customAPI(
      `${domain}my_zone/apply/work_adjustment/pre-special-attendance/?company_id=${requestCompanyId}`,
      "POST",
      payload,
    );
    if (response.ok) {
      showToast("success", response?.responseDetails?.message);
      reCallApi(true);
      onClose();
      reset();
      setValue("button_loader", false);
    } else {
      setValue("button_loader", false);
      showToast("error", response?.message);
    }
  };

  const handleReason = (event) => {
    if (event.target.value.length <= 1024) {
      setValue("reason_for_work_adjustment", event.target.value, {
        shouldValidate: true,
      });
    }
  };

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title={isBreakMode ? "New Break Request" : `${requestTypeLabel} Request`}
      subtitle={
        isBreakMode
          ? "Apply a break or cancel an existing break for a selected date"
          : `Create a ${requestTypeLabel} request for a selected period`
      }
      bodySx={{ backgroundColor: designColors.pageBg }}
      footer={
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            flexDirection: { xs: "column-reverse", sm: "row" },
            width: "100%",
          }}
        >
          <SecondaryButton
            onClick={onClose}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            Cancel
          </SecondaryButton>
          <Tooltip
            title={applyDisabledReason}
            arrow
            disableHoverListener={!applyDisabledReason}
            slotProps={tooltipStyleProps}
          >
            <Box
              component="span"
              sx={{ display: "inline-flex", width: { xs: "100%", sm: "auto" } }}
            >
              <PrimaryButton
                type="submit"
                form="work-adjustment-request-form"
                loading={Boolean(watch("button_loader"))}
                disabled={
                  Boolean(watch("button_loader")) ||
                  Boolean(applyDisabledReason)
                }
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                {isBreakMode ? "Create" : "Apply"}
              </PrimaryButton>
            </Box>
          </Tooltip>
        </Box>
      }
    >
      <form id="work-adjustment-request-form" onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ p: 2 }}>
          <Box sx={childBoxStyle}>
            <RequestDetailHeader
              userDetails={{
                image_url: userDetails?.image_url,
                employee_id: userDetails?.employee_id || "-",
                user_name:
                  userDetails?.first_name ||
                  userDetails?.user_name ||
                  userDetails?.username ||
                  "-",
                department_name:
                  userDetails?.department_name || userDetails?.department || "-",
                designation:
                  userDetails?.designation || userDetails?.designation_name || "-",
              }}
            />
            {isBreakMode ? (
              <Box>
                <Typography sx={{ fontWeight: 600 }}>Break Type</Typography>
                <Typography
                  {...infoStyle}
                  sx={{
                    ...infoStyle.sx,
                    mb: 0,
                  }}
                >
                  Choose whether to apply a new break or cancel an existing one
                </Typography>
                <input
                  type="hidden"
                  {...register("attendance_type", {
                    required: "Break type is required",
                  })}
                />
                <CustomAutoComplete
                  dataList={[
                    { label: "Cancel Break", displayLabel: "Cancel Break" },
                  ]}
                  selectedValue={watch("attendance_type")}
                  updateDataFunction={(event, data) => {
                    if (data) {
                      setValue("attendance_type", data.label, {
                        shouldValidate: true,
                      });
                    }
                  }}
                  placeHolder="Select Break Type"
                  errorMessage={
                    errors?.attendance_type?.message
                      ? errors?.attendance_type?.message
                      : ""
                  }
                  textBoxError={errors?.attendance_type ? true : false}
                />
              </Box>
            ) : (
              <Box>
                <Typography sx={{ fontWeight: 600 }}>
                  WorkAdjustment Type
                </Typography>
                <Typography
                  {...infoStyle}
                  sx={{
                    ...infoStyle.sx,
                    mb: 0,
                  }}
                >
                  Type of Work Adjustment
                </Typography>
                <input
                  type="hidden"
                  {...register("attendance_type", {
                    required: "Attendance type is required",
                  })}
                />
                <Box
                  sx={{
                    mt: 1,
                    minHeight: 40,
                    border: `1px solid ${themes?.miliedLightGray}`,
                    borderRadius: 1,
                    px: 2,
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: "#f8fafc",
                  }}
                >
                  <Typography>{requestTypeLabel}</Typography>
                </Box>
              </Box>
            )}
            {watch("attendance_type") === "Comp off" ? null : (
              <Box>
                <Typography sx={{ fontWeight: 600 }}>Date</Typography>
                <Typography
                  {...infoStyle}
                  sx={{
                    ...infoStyle.sx,
                    mb: 0,
                  }}
                >
                  Select the applicable attendance date
                </Typography>
                <CustomizedDatePicker
                  name="attandance_date"
                  control={control}
                  required
                  requiredMessage="Date is required"
                  placeholder="Select start date" //minDate={}
                  disableFuture
                  handleDisabledFutureDate={true}
                />
              </Box>
            )}
            {watch("attendance_type") === "Break" && (
              <Box>
                <Typography sx={{ fontWeight: 600 }}>Break Category</Typography>
                <Typography {...infoStyle}>
                  Select when the overtime will be considered.
                </Typography>
                <Box>
                  <Controller
                    name="break_category"
                    control={control}
                    defaultValue="OT Break"
                    render={({ field }) => (
                      <FormControl component="fieldset">
                        <RadioGroup row sx={{ gap: 2 }} {...field}>
                          <FormControlLabel
                            value="OT Break"
                            control={<Radio />}
                            label="OT Break"
                          />
                          <FormControlLabel
                            value="Shift Break"
                            control={<Radio />}
                            label="Shift Break"
                          />
                        </RadioGroup>
                      </FormControl>
                    )}
                  />
                </Box>
                <BreakContextInfo />
              </Box>
            )}
            {watch("attendance_type") === "Cancel Break" && (
              <Box>
                <Typography sx={{ fontWeight: 700 }}>
                  Break to Cancel
                </Typography>
                <Typography {...infoStyle}>
                  Select one or more existing break entries to cancel
                </Typography>
                <input
                  type="hidden"
                  {...register("break_to_cancel", {
                    validate: (value) =>
                      watch("attendance_type") !== "Cancel Break" ||
                      (Array.isArray(value) && value.length > 0) ||
                      "Select at least one break to cancel",
                  })}
                />
                <Paper
                  elevation={0}
                  sx={{
                    mt: 1,
                    border: `1px solid ${themes?.miliedLightGray}`,
                    borderRadius: 1,
                    overflow: "hidden",
                  }}
                >
                  <Grid
                    container
                    sx={{
                      px: 1.5,
                      py: 1,
                      backgroundColor: "#f8fafc",
                      display: { xs: "none", sm: "flex" },
                    }}
                  >
                    <Grid item xs={1} />
                    <Grid item xs={3}>
                      <Typography variant="caption" color="text.secondary">
                        Break Start
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="caption" color="text.secondary">
                        Break End
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="caption" color="text.secondary">
                        Duration
                      </Typography>
                    </Grid>
                    <Grid item xs={2}>
                      <Typography variant="caption" color="text.secondary">
                        Type
                      </Typography>
                    </Grid>
                  </Grid>
                  {isCancelBreakLoading ? (
                    [0, 1].map((row) => (
                      <Grid
                        container
                        key={`cancel-break-skeleton-${row}`}
                        sx={{
                          px: 1.5,
                          py: 1.25,
                          alignItems: "center",
                          borderTop: `1px solid ${themes?.miliedLightGray}`,
                        }}
                      >
                        <Grid item xs={1}>
                          <Skeleton variant="rounded" width={18} height={18} />
                        </Grid>
                        {[3, 3, 3, 2].map((size, col) => (
                          <Grid item xs={size} key={col} sx={{ pr: 1 }}>
                            <Skeleton variant="text" width="70%" />
                          </Grid>
                        ))}
                      </Grid>
                    ))
                  ) : cancelBreakOptions.length ? (
                    cancelBreakOptions.map((item) => {
                      const selectedBreaksToCancel =
                        watch("break_to_cancel") || [];
                      const isChecked = selectedBreaksToCancel.some(
                        (entry) => entry?.id === item?.cancel_row_id,
                      );
                      return (
                        <Grid
                          container
                          key={item?.cancel_row_id}
                          sx={{
                            px: 1.5,
                            py: 1,
                            alignItems: "center",
                            borderTop: `1px solid ${themes?.miliedLightGray}`,
                          }}
                        >
                          <Grid item xs={12} sm={1}>
                            <Checkbox
                              size="small"
                              checked={isChecked}
                              onChange={() => {
                                const next = isChecked
                                  ? selectedBreaksToCancel.filter(
                                      (entry) =>
                                        entry?.id !== item?.cancel_row_id,
                                    )
                                  : [
                                      ...selectedBreaksToCancel,
                                      {
                                        id: item?.cancel_row_id,
                                        type: item?.category_code,
                                      },
                                    ];
                                setValue("break_to_cancel", next);
                                clearErrors("break_to_cancel");
                              }}
                            />
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: { xs: "block", sm: "none" } }}
                            >
                              Break Start
                            </Typography>
                            <Typography sx={{ lineHeight: 1.3 }}>
                              {item?.start_time || item?.break_start_time
                                ? dayjs(
                                    item?.start_time || item?.break_start_time,
                                  ).format("HH:mm")
                                : "-"}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: { xs: "block", sm: "none" } }}
                            >
                              Break End
                            </Typography>
                            <Typography sx={{ lineHeight: 1.3 }}>
                              {item?.end_time || item?.break_end_time
                                ? dayjs(
                                    item?.end_time || item?.break_end_time,
                                  ).format("HH:mm")
                                : "-"}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: { xs: "block", sm: "none" } }}
                            >
                              Duration
                            </Typography>
                            <Typography sx={{ lineHeight: 1.3 }}>
                              {formatBreakDuration(item?.duration)}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={2}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: { xs: "block", sm: "none" } }}
                            >
                              Type
                            </Typography>
                            <Typography sx={{ lineHeight: 1.3 }}>
                              {item?.category_label}
                            </Typography>
                          </Grid>
                        </Grid>
                      );
                    })
                  ) : (
                    <Typography
                      sx={{
                        p: 1.5,
                        fontSize: "0.9rem",
                        color: "text.secondary",
                      }}
                    >
                      No breaks found for the selected date
                    </Typography>
                  )}
                </Paper>
                {errors?.break_to_cancel?.message ? (
                  <Typography
                    sx={{ color: "error.main", fontSize: "0.75rem", mt: 0.5 }}
                  >
                    {errors.break_to_cancel.message}
                  </Typography>
                ) : null}
              </Box>
            )}
            {watch("attendance_type") === "Comp off" ? (
              <Box>
                <Typography sx={{ fontWeight: 600 }}>
                  Comp off Earned On
                </Typography>
                <Typography
                  {...infoStyle}
                  sx={{
                    ...infoStyle.sx,
                    mb: 0,
                  }}
                >
                  Select the date employee worked on as weekoff/holiday
                </Typography>
                <CustomizedDatePicker
                  name="earned_date"
                  control={control}
                  required
                  requiredMessage="Comp off earned date is required"
                  placeholder="Select Comp off earned date" //minDate={}
                  onChange={(newValue) => {
                    setValue("earned_date", newValue);
                  }}
                  disableFuture
                  handleDisabledFutureDate={true}
                />
              </Box>
            ) : null}
            {watch("attendance_type") === "Comp off" ? (
              <Box>
                <Typography sx={{ fontWeight: 600 }}>
                  Comp off Requested For
                </Typography>
                <Typography
                  {...infoStyle}
                  sx={{
                    ...infoStyle.sx,
                    mb: 0,
                  }}
                >
                  Select the dare employee wants to avail compensatory off
                </Typography>
                <CustomizedDatePicker
                  name="comp_off_request_date"
                  control={control}
                  required={
                    watch("attendance_type") === "Comp off" ? true : false
                  }
                  requiredMessage="Comp off requested date is required"
                  placeholder="Select Comp Off requested date" //minDate={}
                />
              </Box>
            ) : null}
            {watch("attendance_type") === "Comp off" ||
            watch("attendance_type") === "Cancel Break" ? null : (
              <Box
                display="flex"
                gap={2}
                sx={{ flexDirection: { xs: "column", sm: "row" } }}
              >
                {/* Start Time */}
                <Box flex={1} sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography sx={{ fontWeight: 600 }}>Start Time</Typography>
                  <Typography {...infoStyle}>Select the start time</Typography>
                  <Box sx={{ mt: "auto" }}>
                  <CustomTimePicker
                    name="startTime"
                    control={control}
                    errors={errors}
                    minTime={shiftMinTime}
                    maxTime={shiftMaxTime}
                    shouldDisableTime={disableBreakTime}
                    validateFn={(value) => {
                      if (!value || !dayjs(value).isValid()) {
                        return "Start time is required";
                      }
                      if (
                        watch("attendance_type") === "Break" &&
                        breakCategory === "Shift Break" &&
                        !isTimeWithinShiftWindow(value)
                      ) {
                        return "Start time must fall within shift hours";
                      }
                      if (
                        watch("attendance_type") === "Break" &&
                        breakCategory === "Shift Break" &&
                        breakEndTime &&
                        dayjs(breakEndTime).isValid()
                      ) {
                        const currentStart = getBreakDateTime(value);
                        const currentEnd = getBreakDateTime(breakEndTime);

                        if (!currentStart || !currentEnd) {
                          return true;
                        }

                        const hasOverlap = existingShiftBreaks.some((item) => {
                          const existingStart = dayjs(
                            item?.start_time || item?.break_start_time,
                          );
                          const existingEnd = dayjs(
                            item?.end_time || item?.break_end_time,
                          );

                          if (
                            !existingStart.isValid() ||
                            !existingEnd.isValid()
                          ) {
                            return false;
                          }

                          return (
                            currentStart.isBefore(existingEnd) &&
                            currentEnd.isAfter(existingStart)
                          );
                        });

                        if (hasOverlap) {
                          return "Selected break time overlaps with an existing break";
                        }
                        if (
                          shiftDurationInMinutes !== null &&
                          currentEnd.diff(currentStart, "minute") >
                            shiftDurationInMinutes
                        ) {
                          return "Selected break duration cannot exceed shift hours";
                        }
                      }
                      if (
                        watch("attendance_type") === "Break" &&
                        breakCategory === "OT Break" &&
                        breakEndTime &&
                        dayjs(breakEndTime).isValid()
                      ) {
                        const currentStart = getBreakDateTime(value);
                        const currentEnd = getBreakDateTime(breakEndTime);
                        const otBreakValidation = validateOtBreakRange(
                          currentStart,
                          currentEnd,
                        );

                        if (otBreakValidation) {
                          return otBreakValidation;
                        }
                      }
                      return true;
                    }}
                  />
                  </Box>
                </Box>

                {/* End Time */}
                <Box flex={1} sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography sx={{ fontWeight: 600 }}>End Time</Typography>
                  <Typography {...infoStyle}>Select the end time</Typography>
                  <Box sx={{ mt: "auto" }}>
                  <CustomTimePicker
                    name="endTime"
                    control={control}
                    errors={errors}
                    minTime={shiftMinTime}
                    maxTime={shiftMaxTime}
                    shouldDisableTime={disableBreakTime}
                    validateFn={(value) => {
                      const startTime = watch("startTime");

                      if (!value || !dayjs(value).isValid()) {
                        return "End time is required";
                      }

                      if (startTime) {
                        if (watch("attendance_type") === "Break") {
                          const currentStart = getBreakDateTime(startTime);
                          const currentEnd = getBreakDateTime(value);

                          if (
                            currentStart &&
                            currentEnd &&
                            currentEnd.isBefore(currentStart)
                          ) {
                            return "End time must be after start time";
                          }
                        } else if (dayjs(value).isBefore(dayjs(startTime))) {
                          return "End time must be after start time";
                        }
                      }
                      if (
                        watch("attendance_type") === "Break" &&
                        breakCategory === "Shift Break" &&
                        !isTimeWithinShiftWindow(value)
                      ) {
                        return "End time must fall within shift hours";
                      }
                      if (
                        watch("attendance_type") === "Break" &&
                        breakCategory === "Shift Break" &&
                        startTime &&
                        dayjs(startTime).isValid()
                      ) {
                        const currentStart = getBreakDateTime(startTime);
                        const currentEnd = getBreakDateTime(value);

                        if (!currentStart || !currentEnd) {
                          return true;
                        }

                        const hasOverlap = existingShiftBreaks.some((item) => {
                          const existingStart = dayjs(
                            item?.start_time || item?.break_start_time,
                          );
                          const existingEnd = dayjs(
                            item?.end_time || item?.break_end_time,
                          );

                          if (
                            !existingStart.isValid() ||
                            !existingEnd.isValid()
                          ) {
                            return false;
                          }

                          return (
                            currentStart.isBefore(existingEnd) &&
                            currentEnd.isAfter(existingStart)
                          );
                        });

                        if (hasOverlap) {
                          return "Selected break time overlaps with an existing break";
                        }
                        if (
                          shiftDurationInMinutes !== null &&
                          currentEnd.diff(currentStart, "minute") >
                            shiftDurationInMinutes
                        ) {
                          return "Selected break duration cannot exceed shift hours";
                        }
                      }
                      if (
                        watch("attendance_type") === "Break" &&
                        breakCategory === "OT Break" &&
                        startTime &&
                        dayjs(startTime).isValid()
                      ) {
                        const currentStart = getBreakDateTime(startTime);
                        const currentEnd = getBreakDateTime(value);
                        const otBreakValidation = validateOtBreakRange(
                          currentStart,
                          currentEnd,
                        );

                        if (otBreakValidation) {
                          return otBreakValidation;
                        }
                      }

                      return true;
                    }}
                  />
                  </Box>
                </Box>
                {watch("attendance_type") === "Break" && (
                  <Box
                    flex={1}
                    sx={{ display: "flex", flexDirection: "column" }}
                  >
                    <Typography sx={{ fontWeight: 600 }}>Duration</Typography>
                    <Typography {...infoStyle}>
                      Configured break duration
                    </Typography>
                    <Box
                      sx={{
                        mt: "auto",
                        minHeight: 40,
                        border: `1px solid ${themes?.miliedLightGray}`,
                        borderRadius: 1,
                        px: 2,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Typography>{formatBreakDuration(selectedBreakDuration)}</Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            )}
            <Box>
              <Typography sx={{ fontWeight: 600 }}>Reason</Typography>
              <Typography {...infoStyle}>
                Enter the reason for this attendance request
              </Typography>
              <CustomTextBox
                type="multiline"
                rules={{
                  ...register("reason_for_work_adjustment", {
                    required: "Reason is required",
                    validate: {
                      minLengthNoSpaces: (value) => {
                        if (value?.trim().length < 3 && value !== "") {
                          return minimum3Characters;
                        } else if (value?.trim().length > 255 && value !== "") {
                          return "Maximum length is 255 characters";
                        } else {
                          return true;
                        }
                      },
                      atleastOneCharacterWithSpaceReg: (value) => {
                        if (
                          atleastOneCharacterWithSpace.test(value) ||
                          value === ""
                        ) {
                          return true;
                        } else {
                          return "Atleast one alphabet required";
                        }
                      },
                    },
                  }),
                }}
                selectedValue={watch("reason_for_work_adjustment")}
                updateDataFunction={handleReason}
                placeHolder="Provide a reason for work adjustment"
                errorMessage={
                  errors?.reason_for_work_adjustment?.message
                    ? errors?.reason_for_work_adjustment?.message
                    : ""
                }
                textBoxError={errors?.reason_for_work_adjustment ? true : false}
              />
            </Box>

          </Box>
        </Box>
      </form>
    </GlobalSidePanel>
  );
};
